import React, { useEffect, useState } from 'react';
import { CheckCircle, CloudArrowDown } from '@phosphor-icons/react';
import { subscribeSyncStatus, getSyncStatus } from '../utils/syncStatus';

/**
 * LastSyncedPill — small inline pill showing when the most recent API
 * request landed and whether it was served from cache (offline) or live.
 * Designed to live inside a show header so producers can immediately tell
 * if they're looking at fresh data.
 *
 *   Live network response → green dot · "Live"
 *   Offline (served from cache) → orange dot · "Synced 2:14pm"
 *
 * Updates relative time every 30s.
 */
const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatRelative = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
};

export const LastSyncedPill = ({ className = '' }) => {
  const [status, setStatus] = useState(getSyncStatus());
  const [, setTick] = useState(0);

  useEffect(() => {
    const unsub = subscribeSyncStatus(setStatus);
    const t = setInterval(() => setTick((x) => x + 1), 30 * 1000);
    return () => { unsub(); clearInterval(t); };
  }, []);

  if (!status.lastSyncAt) return null;

  const offline = status.fromCache;
  const dotColor = offline ? 'bg-[#FF9500]' : 'bg-[#34C759]';
  const textColor = offline ? 'text-[#FF9500]' : 'text-[#34C759]';
  const Icon = offline ? CloudArrowDown : CheckCircle;
  const label = offline
    ? `Synced ${formatTime(status.lastSyncAt)} · ${formatRelative(status.lastSyncAt)}`
    : `Live · synced ${formatRelative(status.lastSyncAt)}`;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#0A0A0A]/60 border border-[#262626] text-[10px] font-semibold ${textColor} ${className}`}
      data-testid="last-synced-pill"
      title={offline
        ? `Last synced data, last fetched at ${formatTime(status.lastSyncAt)}.`
        : 'Live data — connected to server.'}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} animate-pulse`} />
      <Icon size={10} weight="bold" />
      {label}
    </span>
  );
};
